import PropTypes from "prop-types";
import { useState } from "react";
import Toggle from "@shared/v2/Toggle";
import Typography from "@shared/v2/Typography";
import TextInput from "@shared/v2/TextInput";
import Button from "@shared/v2/Button";
import Tag from "@shared/v2/Tag";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import CollapsibleWebsiteCard from "../shared/CollapsibleWebsiteCard";
import ImageDropTarget from "../shared/ImageDropTarget";
import { useSiteUuid, usePageUuid } from "../../reducers/websiteReducer/areaPageSelectors";

const ContactFormPanel = ({ data = undefined, setContactCTAData }) => {
  const siteUuid = useSiteUuid();
  const pageUuid = usePageUuid();

  const [tag, setTag] = useState("");
  const tags = data?.tags || [];

  const handleAddTag = () => {
    const newTag = tag.trim();
    if (newTag && !tags.includes(newTag)) {
      setContactCTAData({ ...data, tags: [...tags, newTag] });
    }
    setTag("");
  };

  const handleRemoveTag = (removed) => {
    setContactCTAData({ ...data, tags: tags.filter((item) => item !== removed) });
  };

  const uploadFile = (file) => {
    const formData = new FormData();
    formData.append("image", file);
    formData.append("siteUuid", siteUuid);
    formData.append("pageUuid", pageUuid);
    formData.append("componentUuid", data.uuid);

    caseTransformingAxios.post("/website/upload_image_to_aws", formData).then((res) => {
      setContactCTAData({
        ...data,
        imageUrl: res?.data?.url,
      });
    });
  };

  return (
    <CollapsibleWebsiteCard
      title="Contact Form"
      disabled={!data?.isActive}
      action={
        <Toggle
          checked={data?.isActive}
          onChange={(e) => {
            setContactCTAData({
              ...data,
              isActive: e.target.checked,
            });
          }}
        />
      }
    >
      <div className="tw-flex  tw-gap-[16px] ">
        <div className="tw-flex tw-flex-col tw-flex-1 tw-gap-[16px] tw-p-[16px] tw-border tw-border-solid tw-border-gray-10 tw-rounded-xl">
          <Typography variant="subtitle-m-bold">Tags</Typography>
          <TextInput
            label="Header"
            placeholder=""
            helperText="Large text shown below the sub-header"
            value={data?.kicker}
            onChange={(e) =>
              setContactCTAData({
                ...data,
                kicker: e.target.value,
              })
            }
          />
          <TextInput
            label="Sub-Header"
            placeholder=""
            helperText="Large text shown below the sub-header"
            value={data?.title}
            onChange={(e) =>
              setContactCTAData({
                ...data,
                title: e.target.value,
              })
            }
          />
          <TextInput
            label="Contact Button Text"
            placeholder=""
            value={data?.buttonLabel}
            onChange={(e) =>
              setContactCTAData({
                ...data,
                buttonLabel: e.target.value,
              })
            }
          />

          <div className="tw-flex tw-gap-[8px] tw-items-end">
            <TextInput
              containerClassName="tw-flex tw-flex-1"
              label="Tags"
              placeholder="Enter a tag you would like applied to leads"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
            />
            <Button schema="primary" size="medium" disabled={tag.trim().length <= 0} onClick={handleAddTag}>
              Add Tag
            </Button>
          </div>

          <div className="tw-flex tw-gap-[8px]">
            {tags.map((tagItem) => (
              <Tag key={tagItem} removable onRemove={() => handleRemoveTag(tagItem)}>
                {tagItem}
              </Tag>
            ))}
          </div>
        </div>

        <div className="tw-flex tw-flex-col tw-flex-1 tw-gap-[16px] tw-p-[16px] tw-border tw-border-solid tw-border-gray-10 tw-rounded-xl">
          <Typography variant="subtitle-m-bold">Image</Typography>
          <Typography className="!tw-text-gray-50">Background Image</Typography>
          <div className="tw-flex tw-flex-col tw-max-w-[250px] tw-gap-[24px]">
            {data?.imageUrl && (
              <div className="tw-flex tw-flex-col tw-gap-[8px]">
                <img
                  className="tw-w-full tw-h-[150px] tw-rounded-[8px] tw-object-cover"
                  src={data?.imageUrl}
                  alt="Featured"
                />
                <div className="tw-flex tw-justify-between tw-items-center tw-gap-[8px]">
                  <Button
                    className="tw-flex-1"
                    schema="warning"
                    size="small"
                    onClick={() => setContactCTAData({ ...data, imageUrl: null })}
                  >
                    Delete
                  </Button>
                  <Button
                    className="tw-flex-1"
                    size="small"
                    onClick={() => {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.accept = "image/*";
                      input.onchange = (e) => {
                        const selectedImage = e.target.files[0];
                        uploadFile(selectedImage);
                      };
                      input.click();
                    }}
                  >
                    Replace
                  </Button>
                </div>
              </div>
            )}

            {!data?.imageUrl && <ImageDropTarget onChange={(file) => uploadFile(file)} />}
          </div>
        </div>
      </div>
    </CollapsibleWebsiteCard>
  );
};

ContactFormPanel.propTypes = {
  data: PropTypes.shape({
    uuid: PropTypes.string,
    isActive: PropTypes.bool,
    kicker: PropTypes.string,
    title: PropTypes.string,
    buttonLabel: PropTypes.string,
    imageUrl: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
  }),
  setContactCTAData: PropTypes.func.isRequired,
};

export default ContactFormPanel;
